/**
 * NormalizeUserService
 * 
 * Centralizes all user normalization logic to ensure consistent user objects
 * throughout the application. This service extracts and normalizes user data
 * from Supabase user objects, including roles and business staff roles.
 * 
 * For the business portal, all authenticated users should be business staff
 * with a valid business_id and business staff role.
 */

import type { UserRole, BusinessRole } from '@/types/roles';
import { SUPER_ADMIN_PERMISSIONS } from '@/types/roles';
import { supabase, createClient } from './supabase';
import type { NormalizedUser, BusinessStaffRecord } from '@/types/user-schema';

// Export types used by other components
export interface BusinessRegisterData {
  email: string;
  password: string;
  business_name: string;
  business_type: string;
  is_approved: boolean;
  parish?: string;
  phone?: string;
  role: string;
  business_id?: string;
  registration_token?: string;
}

/**
 * Extract common user metadata with focus on business staff properties
 * 
 * @param user Supabase user object
 * @param existingUser Optional existing user data
 * @returns Common user metadata with business staff properties
 */
const extractUserMetadata = (user: any, existingUser?: Partial<NormalizedUser>) => {
  const email = user.email || 
               existingUser?.email || 
               '';
  const name = user.user_metadata?.name || 
               user.user_metadata?.full_name || 
               existingUser?.name || 
               email.split('@')[0];
  
  // Check for role in multiple locations based on Supabase structure
  // 1. First check user_metadata.role (most common)
  // 2. Then check app_metadata.roles array (used for admin roles)
  // 3. Fall back to existing user role or default to business
  let role = user.user_metadata?.role;
  
  // If no role in user_metadata, check app_metadata.roles array
  if (!role && user.app_metadata?.roles && Array.isArray(user.app_metadata.roles) && user.app_metadata.roles.length > 0) {
    role = user.app_metadata.roles[0];
  }
  
  // If no role in app_metadata, check raw_app_metadata.roles array
  if (!role && user.raw_app_meta_data?.roles && Array.isArray(user.raw_app_meta_data.roles) && user.raw_app_meta_data.roles.length > 0) {
    role = user.raw_app_meta_data.roles[0];
  }
  
  // Fall back to existing user role or default
  role = role || existingUser?.role || 'business';
  
  // Extract business staff role - critical for business portal access
  const businessStaffRole = user.user_metadata?.business_staff_role || 
                            existingUser?.businessStaffRole || 'staff';
  
  // Extract business ID - required for business staff
  const business_id = user.user_metadata?.business_id || 
                      existingUser?.business_id || 
                      '';
  
  // Extract charity ID if present
  const charity_id = user.user_metadata?.charity_id || existingUser?.charity_id || '';
  
  return {
    id: user.id,
    email,
    name,
    role: role as UserRole,
    businessStaffRole: businessStaffRole as BusinessRole,
    business_id,
    charity_id,
    created_at: user.created_at || existingUser?.created_at || new Date().toISOString(),
    updated_at: user.updated_at || existingUser?.updated_at || new Date().toISOString(),
    profilePicture: user.user_metadata?.profilePicture || 
                   user.user_metadata?.avatar_url || 
                   existingUser?.profilePicture,
  };
};

/**
 * Create a business user object with staff role information
 * 
 * @param metadata Base user metadata
 * @param user Supabase user object
 * @param _existingUser Optional existing user data
 * @param commonFields Common fields for all user types
 * @returns NormalizedUser object with business role information
 */
const createBusinessUser = (
  metadata: ReturnType<typeof extractUserMetadata>, 
  user: any, 
  _existingUser?: Partial<NormalizedUser>,
  commonFields?: Partial<NormalizedUser>
): NormalizedUser => {
  // Extract business-specific information - these are stored for reference but not used in the returned object
  // They could be used in the future if we need to display business type or parish in the UI
  
  // Extract or create staff information
  const firstName = user.user_metadata?.first_name || user.user_metadata?.firstName || '';
  const lastName = user.user_metadata?.last_name || user.user_metadata?.lastName || '';
  
  // Create staff record if it doesn't exist
  const staffRecord: BusinessStaffRecord = {
    staff_id: user.id,
    user_id: user.id,
    business_id: metadata.business_id || '',
    email: user.email,
    first_name: firstName,
    last_name: lastName,
    role: metadata.businessStaffRole as BusinessRole || 'staff' as BusinessRole,
    status: 'active',
    created_at: user.created_at || new Date().toISOString(),
    updated_at: user.updated_at || new Date().toISOString(),
    assigned_at: user.created_at || new Date().toISOString()
  };
  
  return {
    ...(commonFields || {}),
    ...metadata,
    role: 'business' as UserRole,
    business_id: metadata.business_id,
    businessStaffRole: metadata.businessStaffRole as BusinessRole || 'staff' as BusinessRole,
    business_access: true,
    charity_access: false,
    staff: staffRecord,
    businessStaff: staffRecord,
  } as NormalizedUser;
};

/**
 * Create a charity user object
 * 
 * @param metadata Base user metadata
 * @param user Supabase user object
 * @param existingUser Optional existing user data
 * @param commonFields Common fields for all user types
 * @returns NormalizedUser object with charity role information
 */
const createCharityUser = (
  metadata: ReturnType<typeof extractUserMetadata>, 
  user: any, 
  existingUser?: Partial<NormalizedUser>,
  commonFields?: Partial<NormalizedUser>
): NormalizedUser => {
  // Extract charity-specific information
  const charity_id = user.user_metadata?.charity_id || metadata.charity_id || '';
  
  // Extract profile information
  const firstName = user.user_metadata?.first_name || user.user_metadata?.firstName || '';
  const lastName = user.user_metadata?.last_name || user.user_metadata?.lastName || '';
  
  return {
    ...(commonFields || {}),
    ...metadata,
    role: 'charity' as UserRole,
    charity_id,
    business_id: user.user_metadata?.business_id || existingUser?.business_id || '',
    businessStaffRole: undefined,
    business_access: false,
    charity_access: true,
    name: commonFields?.name || `${firstName} ${lastName}`.trim() || user.user_metadata?.name || '',
    profilePicture: commonFields?.profilePicture || user.user_metadata?.avatar_url || '',
  } as NormalizedUser;
};

// Regular users are not used in business portal context

/**
 * Normalizes a Supabase user object into our application's NormalizedUser type
 * with focus on business staff authentication
 * 
 * @param user The Supabase user object to normalize
 * @param existingUser Optional existing user data to merge with
 * @returns Normalized user object that conforms to our NormalizedUser type
 */
export const normalizeUser = (user: any, existingUser?: Partial<NormalizedUser>): NormalizedUser => {
  if (!user) {
    throw new Error('Cannot normalize undefined or null user');
  }

  // Log the user data for debugging
  console.debug('Normalizing user for business portal:', { 
    id: user.id, 
    email: user.email, 
    metadata: user.user_metadata,
    business_id: user.user_metadata?.business_id,
    business_staff_role: user.user_metadata?.business_staff_role,
    existingUser: existingUser ? { 
      id: existingUser.id, 
      role: existingUser.role,
      businessStaffRole: existingUser.businessStaffRole,
      business_id: existingUser.business_id
    } : 'none'
  });

  // Extract common metadata with business staff focus
  const metadata = extractUserMetadata(user, existingUser);
  const role = metadata.role;
  
  // Set up common properties for all user types
  const firstName = user.user_metadata?.first_name || user.user_metadata?.firstName || '';
  const lastName = user.user_metadata?.last_name || user.user_metadata?.lastName || '';
  const fullName = user.user_metadata?.full_name || user.user_metadata?.fullName || `${firstName} ${lastName}`.trim();
  
  // Set up common fields for all user types
  const commonFields: Partial<NormalizedUser> = {
    // Core user identity
    id: user.id,
    email: user.email || '',
    created_at: user.created_at || new Date().toISOString(),
    updated_at: user.updated_at || new Date().toISOString(),
    
    // Raw metadata for backward compatibility
    user_metadata: user.user_metadata || {},
    app_metadata: user.app_metadata || {},
    
    // Common access properties for backward compatibility
    name: fullName || user.user_metadata?.name || '',
    profilePicture: user.user_metadata?.avatar_url || user.user_metadata?.profilePicture || '',
    
    // Profile data
    profile: {
      first_name: firstName,
      last_name: lastName,
      full_name: fullName,
      phone: user.user_metadata?.phone || '',
      avatar_url: user.user_metadata?.avatar_url || ''
    },
    
    // Access flags
    business_access: user.app_metadata?.business_access || false,
    charity_access: user.app_metadata?.charity_access || false,
    super_admin_access: user.app_metadata?.super_admin_access || false
  };

  // For business portal, prioritize business users
  // All users should be business staff with a valid business_id
  if (role === 'business') {
    return createBusinessUser(metadata, user, existingUser as Partial<NormalizedUser>, commonFields);
  } else if (role === 'charity') {
    return createCharityUser(metadata, user, existingUser as Partial<NormalizedUser>, commonFields);
  } else if (role === 'admin' || role === 'super_admin') {
    // Admin users have elevated privileges but should still have business context
    // For the business portal, ensure they have access to business data
    console.debug(`Normalizing ${role} user with business context`);
    
    // Get business_id from metadata or from user_metadata
    const business_id = metadata.business_id || user.user_metadata?.business_id || '';
    
    // Apply appropriate permissions based on role
    const permissions = role === 'super_admin' ? SUPER_ADMIN_PERMISSIONS : {
      ...SUPER_ADMIN_PERMISSIONS,
      isSuperAdmin: false // Admin has all permissions except isSuperAdmin
    };
    
    // Create staff record if it doesn't exist
    const staffRecord: BusinessStaffRecord = {
      staff_id: user.id,
      user_id: user.id,
      business_id: business_id,
      email: user.email,
      first_name: firstName,
      last_name: lastName,
      role: 'manager' as BusinessRole,
      status: 'active',
      created_at: user.created_at || new Date().toISOString(),
      updated_at: user.updated_at || new Date().toISOString(),
      assigned_at: user.created_at || new Date().toISOString()
    };
    
    return { 
      ...commonFields,
      ...metadata, 
      role: role as UserRole, 
      business_id, 
      businessStaffRole: metadata.businessStaffRole as BusinessRole || 'manager' as BusinessRole,
      staff: staffRecord,
      businessStaff: staffRecord,
      ...permissions
    } as NormalizedUser;
  } else {
    // Even regular users in business portal should have business context
    // This is a fallback, but ideally all users should have business role
    console.warn('Non-business user in business portal:', role);
    return createBusinessUser({
      ...metadata,
      role: 'business' as UserRole
    }, user, existingUser as Partial<NormalizedUser>, commonFields);
  }
};

/**
 * Fetches fresh user metadata from Supabase and normalizes it
 * 
 * @param user Basic user data with at least an ID
 * @param existingUser Optional existing user data to merge with
 * @returns Promise resolving to normalized user
 */
// Export services for backward compatibility
export const authService = {
  registerBusiness: async (registerData: BusinessRegisterData): Promise<{ user: NormalizedUser | null }> => {
    try {
      const supabase = await createClient();
      
      // Register the user with Supabase
      const { data, error } = await supabase.auth.signUp({
        email: registerData.email,
        password: registerData.password,
        options: {
          data: {
            name: registerData.business_name,
            role: 'business',
            business_id: registerData.business_id,
            business_name: registerData.business_name,
            business_type: registerData.business_type,
            parish: registerData.parish,
            phone: registerData.phone,
            is_approved: registerData.is_approved
          }
        }
      });
      
      if (error) throw error;
      
      return { user: data.user ? normalizeUser(data.user) : null };
    } catch (error: any) {
      console.error('Register business error:', error);
      throw error;
    }
  }
};

export const businessStaffService = {
  // Add basic business staff methods
  getStaff: async (businessId: string) => {
    try {
      const supabase = await createClient();
      const { data, error } = await supabase
        .from('business_staff')
        .select('*')
        .eq('business_id', businessId);
      
      if (error) throw error;
      return data || [];
    } catch (error: any) {
      console.error('Get staff error:', error);
      return [];
    }
  },
  
  updateStaffRole: async (userId: string, businessId: string, role: BusinessRole) => {
    try {
      const supabase = await createClient();
      const { data, error } = await supabase
        .from('business_staff')
        .update({ role })
        .eq('user_id', userId)
        .eq('business_id', businessId)
        .select()
        .single();
      
      if (error) throw error;
      return data;
    } catch (error: any) {
      console.error('Update staff role error:', error);
      throw error;
    }
  }
};
export const userService = {
  getProfile: async (): Promise<NormalizedUser | null> => {
    const { data: { user } } = await supabase.auth.getUser();
    return user ? normalizeUser(user) : null;
  },
  updateProfile: async (_userData: any): Promise<NormalizedUser | null> => {
    // This is a simplified version - the actual implementation would need more logic
    // Using the existing refreshAndNormalizeUser function
    const { data: { user } } = await supabase.auth.getUser();
    return user ? refreshAndNormalizeUser(user) : null;
  }
};

export const refreshAndNormalizeUser = async (user: { id: string }, existingUser?: Partial<NormalizedUser>): Promise<NormalizedUser> => {
  try {
    // Fetch fresh user metadata directly from Supabase
    const { data: userData, error } = await supabase.auth.admin.getUserById(user.id);
    
    if (error) {
      throw error;
    }
    
    // CRITICAL: Get user profile data from the profiles table
    // This is the primary source of user role and business information
    let profileData = null;
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', user.id)
        .single();
      
      if (error) {
        console.warn('Could not fetch profile data by id:', error);
        // Note: The profiles table doesn't have an email field according to the schema,
        // so we can't do an email-based lookup
      } else {
        profileData = data;
        console.log('Found profile data using id lookup');
      }
    } catch (err) {
      console.error('Error during profile lookup:', err);
    }
    
    // Get additional user data from the users table if needed (secondary)
    const { data: additionalUserData } = await supabase
      .from('users')
      .select('*')
      .eq('user_id', user.id)
      .single();
    
    // Get business data if applicable
    // Use business_id from profiles table first, then fall back to user metadata
    const businessId = profileData?.business_id || userData.user.user_metadata?.business_id;
    let businessData = null;
    if (businessId) {
      const { data: business } = await supabase
        .from('businesses')
        .select('*')
        .eq('business_id', businessId)
        .single();
      businessData = business;
    }
    
    // Get staff data if applicable
    // Use business_id from profiles table first, then fall back to user metadata
    let staffData = null;
    if (businessId) {
      const { data: staff } = await supabase
        .from('business_staff')
        .select('*')
        .eq('user_id', user.id)
        .eq('business_id', businessId)
        .single();
      staffData = staff;
    }
    
    // Merge all data with priority given to profiles table data
    const freshUserData = {
      ...userData.user,
      ...additionalUserData,
      // Explicitly include profile data with highest priority
      ...(profileData && {
        role: profileData.role,
        businessStaffRole: profileData.business_staff_role,
        business_id: profileData.business_id,
        name: profileData.full_name,
        profilePicture: profileData.avatar_url
      }),
      business: businessData,
      staff: staffData as BusinessStaffRecord
    };
    
    console.log('User data after refresh:', {
      id: freshUserData.id,
      email: freshUserData.email,
      role: freshUserData.role || profileData?.role,
      business_id: freshUserData.business_id || profileData?.business_id,
      businessStaffRole: freshUserData.businessStaffRole || profileData?.business_staff_role
    });
    
    // Merge fresh data with existing user data
    return normalizeUser({
      ...user,
      ...freshUserData,
    }, existingUser);
  } catch (error) {
    console.error('Error refreshing user metadata:', error);
    // Fall back to basic normalization if refresh fails
    return normalizeUser(user, existingUser);
  }
};
